// Sign-up funnel (restored 2026-09-08). The v2 editor shipped with none of the
// classic editor's account prompts and sign-ups fell ~70% on flat traffic:
//   1. Import Data (batch) needs a free account; free accounts get
//      BATCH_FREE_LIMIT batch exports a month, then the Pro upsell
//      (classic checkBatchUsage, snapshot L3735).
//   2. Every export/print is logged (export_logs for users, a localStorage
//      counter for anonymous) — classic logExport, L2138.
//   3. After the 2nd anonymous print/export, a one-time "save your designs to
//      the cloud" nudge with a Create-free-account button (classic
//      maybeShowCloudNudge, L9470).
import { state } from './state'
import { supabase } from './auth/supabase'
import { checkBatchUsage } from './billing'
import { openAuth } from './auth/auth-ui'
import { openUpgrade } from './panels/upgradeModal'
import { el, toast } from './ui/dom'
import { trackEvent } from './analytics'

const COUNT_KEY = 'olm_export_count'
const NUDGE_KEY = 'olm_cloud_nudge_shown'

export const BATCH_SIGNUP_MESSAGE = 'Create a free account to print labels from a spreadsheet — free accounts get 5 batch exports a month.'

/** Record an export/print: export_logs row for users (feeds the batch quota), localStorage counter for anonymous. */
export function logExport(actionType: 'batch' | 'export' | 'print'): void {
  const user = state.currentUser
  if (user) {
    void supabase.from('export_logs').insert({ user_id: user.id, action_type: actionType }).then(() => { /* fire-and-forget */ }, () => { /* offline / RLS — quota fails open */ })
    return
  }
  try { localStorage.setItem(COUNT_KEY, String(anonExportCount() + 1)) } catch { /* storage blocked */ }
}

export function anonExportCount(): number {
  try { return parseInt(localStorage.getItem(COUNT_KEY) ?? '0', 10) || 0 } catch { return 0 }
}

/**
 * Gate for batch (Import Data / batch export). Anonymous → sign-up modal;
 * free tier over quota → Pro upsell. Returns true when the action may proceed.
 */
export async function requireBatchAccess(): Promise<boolean> {
  if (!state.currentUser) {
    trackEvent('signup_prompt', { source: 'batch' })
    openAuth('signup', { message: BATCH_SIGNUP_MESSAGE })
    return false
  }
  const usage = await checkBatchUsage()
  if (usage.allowed) return true
  trackEvent('upgrade_prompt', { source: 'batch_limit' })
  openUpgrade('batch_labels')
  return false
}

/** Anonymous users only: once, after their 2nd export/print, offer a free account. */
export function maybeCloudNudge(): void {
  if (state.currentUser) return
  try { if (localStorage.getItem(NUDGE_KEY)) return } catch { return }
  if (anonExportCount() < 2) return
  try { localStorage.setItem(NUDGE_KEY, '1') } catch { /* ignore */ }
  setTimeout(showCloudNudge, 2500) // after the export toast has faded
}

export function showCloudNudge(): void {
  document.getElementById('cloudNudge')?.remove()
  const card = el('div', 'toast show')
  card.id = 'cloudNudge'
  card.dataset.testid = 'cloud-nudge'
  card.style.cssText = 'display:flex;align-items:center;gap:12px;max-width:380px;padding:12px 14px'
  const icon = el('div', '', '☁️'); icon.style.fontSize = '22px'
  const text = el('div')
  text.append(el('div', 'text-[13px] font-semibold text-slate-100', 'Save your designs to the cloud'),
    el('div', 'text-[12px] text-slate-400', 'Free account · open your labels on any device'))
  const cta = el('button', 'tb-btn primary', 'Create free account') as HTMLButtonElement
  cta.type = 'button'
  cta.dataset.testid = 'cloud-nudge-signup'
  cta.addEventListener('click', () => { card.remove(); trackEvent('signup_prompt', { source: 'cloud_nudge' }); openAuth('signup') })
  const close = el('button', 'text-slate-500 hover:text-slate-300 text-sm', '✕') as HTMLButtonElement
  close.type = 'button'
  close.setAttribute('aria-label', 'Dismiss')
  close.addEventListener('click', () => card.remove())
  card.append(icon, text, cta, close)
  document.body.append(card)
  trackEvent('cloud_nudge_shown')
  setTimeout(() => card.remove(), 20_000)
}

// Re-export for callers that only need a toast-style hint.
export { toast as _toast }
